'use client';

import { mergeProps, useRender } from '@base-ui/react';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cn } from '@workspace/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
    "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:ring-3 disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-3 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                default: 'bg-brand text-brand-foreground hover:bg-brand/80',
                outline:
                    'border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground shadow-xs',
                'outline-brand':
                    'border-brand text-brand bg-background hover:bg-brand hover:text-brand-foreground aria-expanded:bg-brand aria-expanded:text-brand-foreground shadow-xs',
                'outline-primary':
                    'border-brand text-brand bg-background hover:bg-brand hover:text-brand-foreground aria-expanded:bg-brand aria-expanded:text-brand-foreground shadow-xs',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
                ghost: 'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
                'ghost-brand':
                    'text-brand hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
                'ghost-primary':
                    'text-brand hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
                destructive:
                    'bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 text-destructive focus-visible:border-destructive/40',
                link: 'text-brand underline-offset-4 hover:underline',
            },
            size: {
                default:
                    'h-9 gap-1.5 px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
                xs: "h-6 gap-1 rounded-[min(var(--radius-md),8px)] px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
                sm: 'h-8 gap-1 rounded-[min(var(--radius-md),10px)] px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
                lg: 'h-10 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
                xl: 'h-12 gap-2 px-3 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4',
                icon: 'size-9',
                'icon-xs':
                    "size-6 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
                'icon-sm':
                    'size-8 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-md',
                'icon-lg': 'size-10',
                'icon-xl': "size-12 [&_svg:not([class*='size-'])]:size-6",
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
);

function Button({
    className,
    variant = 'default',
    size = 'default',
    ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
    return (
        <ButtonPrimitive
            data-slot="button"
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
}

type LinkButtonProps = useRender.ComponentProps<'a'> &
    VariantProps<typeof buttonVariants>;

function LinkButton({
    className,
    variant = 'default',
    size = 'default',
    render,
    ...props
}: LinkButtonProps) {
    return useRender({
        defaultTagName: 'a',
        render,
        props: mergeProps(
            {
                'data-slot': 'button',
                className: cn(buttonVariants({ variant, size, className })),
            },
            props
        ),
    });
}

export { Button, buttonVariants, LinkButton };
